/**
 * El tamaño de la letra que se lee, guardado en el aparato.
 *
 * ── Qué mueve, y qué no ─────────────────────────────────────────────────────
 *
 * Mueve `--escala-lectura`, que multiplica el tamaño de los mensajes, las
 * tarjetas de aprobación y los avisos. **No toca los botones, las pestañas ni
 * las cifras**: ahí no se lee, se mira, y agrandarlos sólo quita sitio a lo
 * que sí hay que leer.
 *
 * ── Por qué en el aparato y no en la cuenta ─────────────────────────────────
 *
 * Porque el que lo necesita es el ojo delante de ESTA pantalla: el móvil en la
 * mano a medio metro y el portátil a setenta centímetros no piden lo mismo, y
 * son la misma cuenta. Guardarlo en el servidor obligaría a que los dos fueran
 * iguales.
 *
 * ── Y por qué multiplica en vez de fijar un número ──────────────────────────
 *
 * Los tamaños base van en `rem`, o sea que ya respetan lo que el dueño del
 * aparato eligió en su sistema. Esto va ENCIMA: quien tenga el móvil en
 * «texto grande» y además suba aquí, ve las dos cosas sumadas. Fijar píxeles
 * aquí sería volver a decidir por él, que es lo que se vino a quitar.
 */

const CLAVE = "bilbobot.escalaLectura";

/** Los pasos del A- / A+. El 1 es el de siempre, y está en la lista a propósito. */
export const ESCALAS = [0.9, 1, 1.15, 1.3, 1.45] as const;

export const ESCALA_POR_DEFECTO = 1;

/** La escala guardada, o la de siempre. Nunca lanza: sin `localStorage` se sigue. */
export function escalaGuardada(): number {
  try {
    const crudo = localStorage.getItem(CLAVE);
    if (!crudo) return ESCALA_POR_DEFECTO;
    const n = Number(crudo);
    /*
     * Sólo un valor de la lista.
     *
     * Un número raro en el almacén —de una versión anterior, o de alguien
     * toqueteando— dejaría la app con la letra a 4 y sin forma de volver desde
     * la propia pantalla, porque los botones también se irían de sitio.
     */
    return (ESCALAS as readonly number[]).includes(n) ? n : ESCALA_POR_DEFECTO;
  } catch {
    return ESCALA_POR_DEFECTO;
  }
}

/**
 * Donde se escribe la escala: la raíz del documento.
 *
 * Va como parámetro con valor por defecto para poder probar las decisiones
 * —qué escala vale, cuál es la siguiente— sin montar un DOM entero. El
 * proyecto no tiene jsdom, y añadirlo por una línea de estilo sería pagar una
 * dependencia por no escribir un parámetro.
 */
export interface Raiz {
  style: { setProperty(nombre: string, valor: string): void };
}

/** La deja puesta en la página y guardada. Devuelve la que quedó. */
export function ponerEscala(escala: number, raiz?: Raiz): number {
  const buena = (ESCALAS as readonly number[]).includes(escala) ? escala : ESCALA_POR_DEFECTO;
  const donde = raiz ?? (typeof document === "undefined" ? null : document.documentElement);
  donde?.style.setProperty("--escala-lectura", String(buena));
  try {
    localStorage.setItem(CLAVE, String(buena));
  } catch {
    /* En privado o con el almacén lleno no se guarda, pero se ve igual. */
  }
  return buena;
}

/** La siguiente hacia arriba o hacia abajo. En los extremos, se queda. */
export function siguienteEscala(actual: number, hacia: "mas" | "menos"): number {
  const i = (ESCALAS as readonly number[]).indexOf(actual);
  const desde = i === -1 ? (ESCALAS as readonly number[]).indexOf(ESCALA_POR_DEFECTO) : i;
  const siguiente = hacia === "mas" ? desde + 1 : desde - 1;
  return ESCALAS[Math.min(Math.max(siguiente, 0), ESCALAS.length - 1)]!;
}

/**
 * Se aplica al arrancar, antes de pintar.
 *
 * Si se hiciera dentro de un componente, la primera pantalla saldría con el
 * tamaño de siempre y daría un salto al montarse. Un salto al abrir la app
 * cien veces al día se nota más que el propio tamaño.
 */
export function aplicarAlArrancar(raiz?: Raiz): void {
  const guardada = escalaGuardada();
  if (guardada !== ESCALA_POR_DEFECTO) ponerEscala(guardada, raiz);
}
